import { useImagePicker } from "@repo/components/src/hooks/image";
import { createFile } from "@repo/components/src/hooks/image/useImagePicker";
import { Box } from "@repo/components/src/ui/box";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { Icon } from "@repo/components/src/ui/icon";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import ReactNativeFile from "@repo/graphql/client/file";
import { MediaType } from "expo-image-picker";
import { AlertCircleIcon, FileImage } from "lucide-react-native";
import { ComponentProps, useCallback } from "react";
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
} from "react-hook-form";
import * as yup from "yup";
import { useDraggableFiles } from "../../hooks/drag";
import { VStack } from "../../ui/vstack";
import { draftFileSchema } from "../../utils/yup";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputProps } from "../types";
import DraftImageCard from "./draftImageCard";

type FileSchema = yup.InferType<typeof draftFileSchema>;

type MultiplePros<T extends FieldValues> =
  | ({ multiple: true } & Omit<
      FormInputProps<T, FileSchema[] | null>,
      "size" | "placeholder" | "required"
    >)
  | ({ multiple: false } & Omit<
      FormInputProps<T, FileSchema | null>,
      "size" | "placeholder" | "required"
    >);

type CampaignDraftDragFileProps<T extends FieldValues> = MultiplePros<T> & {
  iconSize?: ComponentProps<typeof Icon>["size"];
  size?: ComponentProps<typeof FormControl>["size"];
  iconClassName?: string;
  accept?: MediaType | MediaType[];
  helperText?: string;
};

export default function CampaignDraftDragFile<TFieldValues extends FieldValues>(
  props: CampaignDraftDragFileProps<TFieldValues>,
) {
  const {
    control,
    name,
    label,
    labelSize,
    disabled,
    className,
    iconSize = "md",
    iconClassName = "",
    accept = "images",
    size = "md",
    helperText,
    multiple = true,
  } = props;

  const { isDragging, dropToRnFiles, draggableProps } = useDraggableFiles();

  const { onPress } = useImagePicker({
    options: {
      mediaTypes: accept,
      allowsMultipleSelection: multiple,
      selectionLimit: multiple ? 10 : undefined,
    },
  });

  const onDrop = useCallback(
    async (
      e: React.DragEvent<HTMLDivElement>,
      onChange: ControllerRenderProps<TFieldValues>["onChange"],
      value: ControllerRenderProps<TFieldValues>["value"],
    ) => {
      const newFiles = await dropToRnFiles(e);
      const validFiles = newFiles.filter((file) =>
        accept === "images" ? file.type.startsWith("image/") : true,
      );
      validFiles.length > 0 &&
        onChange(multiple ? [...(value || []), ...validFiles] : validFiles[0]);
    },
    [dropToRnFiles, multiple, accept],
  );

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value, ref }, fieldState: { error } }) => {
        return (
          <FormControl
            isInvalid={!!error}
            isDisabled={disabled}
            className={className}
            size={size}
          >
            {!!label && (
              <FormControlLabel>
                <FormControlLabelText size={labelSize}>
                  {label}
                </FormControlLabelText>
              </FormControlLabel>
            )}
            <Pressable
              onPress={async () => {
                const newFile = await onPress();
                newFile &&
                  onChange(
                    multiple
                      ? [...(value || []), ...newFile.map(createFile)]
                      : createFile(newFile[0]),
                  );
              }}
              ref={focusScrollRef(ref)}
            >
              {(pressableProps: any) => (
                <div
                  {...draggableProps}
                  onDrop={(e: React.DragEvent<HTMLDivElement>) =>
                    onDrop(e, onChange, value)
                  }
                >
                  <Box
                    className={`border-2 border-dashed border-brand-300 rounded-lg py-8 bg-brand-50 ${
                      pressableProps.pressed || isDragging ? "opacity-50" : ""
                    }`}
                  >
                    <Box className="flex items-center justify-center space-y-2">
                      <Icon
                        as={FileImage}
                        size={iconSize}
                        className={`text-typography-400 ${iconClassName}`}
                      />
                      <Text className="text-typography-500 text-center">
                        {helperText || "Arrastra y suelta un archivo aquí"}
                      </Text>
                    </Box>
                  </Box>
                </div>
              )}
            </Pressable>
            <VStack space="sm" className="mt-2">
              {multiple ? (
                value?.map((file: ReactNativeFile) => {
                  return (
                    <DraftImageCard
                      key={file._id}
                      file={file}
                      onChange={onChange}
                      value={value}
                    />
                  );
                })
              ) : (
                <></>
              )}
            </VStack>
            <FormControlError>
              <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
              <FormControlErrorText>{error?.message}</FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}
